import React, { useState } from "react";
import styles from "./styles.module.scss";
import { ArrowIcon, ChevronRight } from "../Icons";

interface MarcasV2Props {
  signupLink: string;
}

type Marca = {
  id: string;
  nome: string;
  logo: string;
  logoAlt: string;
  produto: string;
  site: string;
  descricao: React.ReactNode;
};

const GRUPO_URL = "https://www.amopromo.com/";

const marcaLink = (site: string, nome: string) => (
  <a href={site} target="_blank" rel="noreferrer">
    {nome}
  </a>
);

const grupoLink = (
  <a href={GRUPO_URL} target="_blank" rel="noreferrer">
    Grupo Amo Promo
  </a>
);

const MARCAS: Marca[] = [
  {
    id: "seguros",
    nome: "Seguros Promo",
    logo: "/images-capa2/marcas/seguros.png",
    logoAlt: "logo seguros promo",
    produto: "Seguro Viagem",
    site: "https://www.segurospromo.com.br/",
    descricao: (
      <>
        Sobre a marca: O{" "}
        {marcaLink("https://www.segurospromo.com.br/", "Seguros Promo")} faz
        parte do {grupoLink}, sendo o maior comparador de seguro viagem do
        Brasil. A plataforma busca, em tempo real, os melhores planos e
        seguradoras de viagem para destinos do mundo todo. Garantindo sempre o
        melhor atendimento e desconto.
      </>
    ),
  },
  {
    id: "vaidepromo",
    nome: "Vai de Promo",
    logo: "/images-capa2/marcas/vaidepromo.png",
    logoAlt: "logo vai de promo",
    produto: "Passagens Aéreas",
    site: "https://www.vaidepromo.com.br/",
    descricao: (
      <>
        O comparador de passagens aéreas,{" "}
        {marcaLink("https://www.vaidepromo.com.br/", "Vai de Promo")}, também
        integra o {grupoLink}, com a proposta de buscar e apresentar, em tempo
        real, as melhores ofertas de voos nacionais e internacionais. Com
        preços e atendimento que só a Amo Promo oferece!
      </>
    ),
  },
  {
    id: "travelbds",
    nome: "Travel BDS",
    logo: "/images-capa2/marcas/travelbds.png",
    logoAlt: "logo travel bds",
    produto: "eSIM Internacional",
    site: "https://travelbds.com/",
    descricao: (
      <>
        A {marcaLink("https://travelbds.com/", "Travel BDS")} nasceu para
        transformar a experiência de conexão durante viagens internacionais. A
        marca oferece eSIMs, chips virtuais que permitem acesso à internet de
        forma prática, rápida e sem a necessidade de chip físico.
      </>
    ),
  },
  {
    id: "fluency",
    nome: "Fluency Pass",
    logo: "/images-capa2/marcas/fluency.png",
    logoAlt: "logo fluency pass",
    produto: "Inglês para Viagem",
    site: "https://fluencypass.com/",
    descricao: (
      <>
        Muito mais que uma escola de inglês online, a{" "}
        {marcaLink("https://fluencypass.com/", "Fluency Pass")} é a maior
        referência em intercâmbios da América Latina e pioneira no método de
        ciclo completo (teoria, prática e imersão). Focada em resultados para a
        carreira, a plataforma une tecnologia de ponta e interação humana para
        transformar o seu inglês com segurança, consistência e flexibilidade
        total.
      </>
    ),
  },
  {
    id: "getmalas",
    nome: "GetMalas",
    logo: "/images-capa2/marcas/getmalas.png",
    logoAlt: "logo getmalas",
    produto: "Aluguel de malas",
    site: "https://getmalas.com.br/",
    descricao: (
      <>
        O {marcaLink("https://getmalas.com.br/", "GetMalas")} é a maior
        plataforma de aluguel de malas de viagem do Brasil, focada em economia
        compartilhada e praticidade. O serviço permite utilizar malas de alta
        qualidade pagando apenas pelo período da viagem, eliminando custos de
        compra e problemas de armazenamento. Com entrega e retirada em
        domicílio, a empresa oferece uma solução sustentável que inclui
        higienização completa e seguro contra danos.
      </>
    ),
  },
];

const MarcasV2: React.FC<MarcasV2Props> = ({ signupLink }) => {
  const [ativa, setAtiva] = useState<string>(MARCAS[0].id);
  const marca = MARCAS.find((m) => m.id === ativa) ?? MARCAS[0];

  const selecionar = (id: string, botao: HTMLButtonElement) => {
    setAtiva(id);
    if (window.matchMedia("(max-width: 900px)").matches) {
      window.setTimeout(() => {
        botao.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  const renderPanel = (extra: string) => (
    <div className={`${styles.panel} ${extra}`}>
      <div className={styles.panelLogo}>
        <img src={marca.logo} alt={marca.logoAlt} />
      </div>
      <p className={styles.panelTitle}>{marca.nome}</p>
      <span className={styles.badge}>
        Produto ou serviço:&nbsp;<strong>{marca.produto}</strong>
      </span>
      <p>{marca.descricao}</p>
    </div>
  );

  return (
    <section id="marcas" className={styles.marcas}>
      <div className={styles.head}>
        <h2>
          Conheça <span>nossas marcas e produtos!</span>
        </h2>
        <p>
          Atualmente, o Parceiros Promo trabalha com um portfólio diversificado
          de produtos ligados ao turismo e viagem, incluindo:
        </p>
      </div>

      <div className={styles.body}>
        <div className={styles.list}>
          {MARCAS.map((m) => (
            <React.Fragment key={m.id}>
              <button
                type="button"
                className={`${styles.item} ${
                  m.id === ativa ? styles.itemActive : ""
                }`}
                aria-expanded={m.id === ativa}
                onClick={(e) => selecionar(m.id, e.currentTarget)}
              >
                <span className={styles.itemInner}>
                  <span className={styles.itemLogo}>
                    <img src={m.logo} alt={m.logoAlt} />
                  </span>
                  <h3 className={styles.itemName}>{m.nome}</h3>
                </span>
                <span
                  className={`${styles.chev} ${
                    m.id === ativa ? styles.chevOpen : ""
                  }`}
                >
                  <ChevronRight size={20} color="#644ED0" />
                </span>
              </button>
              {m.id === ativa && renderPanel(styles.panelMobileOnly)}
            </React.Fragment>
          ))}
        </div>

        {renderPanel(styles.panelDesktopOnly)}
      </div>

      <div className={styles.foot}>
        <p>
          Já pensou em lucrar com marcas renomadas do turismo? Divulgue nossos
          links de parceiro e garanta sua comissão em cada venda feita!
        </p>
        <a href={signupLink} className={styles.button} title="Quero ser um Parceiro">
          <span>Quero ser um Parceiro!</span>
          <ArrowIcon />
        </a>
      </div>
    </section>
  );
};

export default MarcasV2;
